import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Clock,
  CheckCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';

interface WarrantyAsset {
  id: string;
  name: string;
  assetTag: string;
  serialNumber?: string;
  warrantyExpiration: string;
  daysUntilExpiry: number;
  status: 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED';
  category?: { name: string };
  brand?: { name: string };
  vendor?: { name: string };
}

export default function WarrantyAlertsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['warranty-alerts', { page, search, filter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (filter) params.append('filter', filter);
      const response = await api.get(`/warranty/alerts?${params.toString()}`);
      return response.data;
    },
  });

  const assets: WarrantyAsset[] = data?.data || [];
  const pagination = data?.pagination;
  const stats = data?.stats;

  const getWarrantyBadge = (asset: WarrantyAsset) => {
    if (asset.daysUntilExpiry < 0) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
          <ShieldX className="w-3 h-3" />
          Expired {Math.abs(asset.daysUntilExpiry)} days ago
        </span>
      );
    }
    if (asset.daysUntilExpiry <= 30) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
          <ShieldAlert className="w-3 h-3" />
          Expires in {asset.daysUntilExpiry} days
        </span>
      );
    }
    if (asset.daysUntilExpiry <= 90) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
          <AlertTriangle className="w-3 h-3" />
          Expires in {asset.daysUntilExpiry} days
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
        <ShieldCheck className="w-3 h-3" />
        {asset.daysUntilExpiry} days remaining
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Warranty Alerts</h1>
        <p className="text-muted-foreground">Monitor warranty expirations and plan renewals</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Under Warranty</p>
                <p className="text-2xl font-bold">{stats?.active || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring in 90 Days</p>
                <p className="text-2xl font-bold">{stats?.expiring90 || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiring in 30 Days</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.expiring30 || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <ShieldX className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expired</p>
                <p className="text-2xl font-bold text-red-600">{stats?.expired || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Urgent Alerts */}
      {(stats?.expiring30 || 0) > 0 && (
        <Card className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <div>
                <p className="font-medium text-orange-800 dark:text-orange-400">
                  {stats?.expiring30} asset(s) have warranties expiring within 30 days
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-500">
                  Consider renewing warranties or planning for replacements
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by asset name, tag, or serial number..."
                className="pl-10"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Warranties</option>
              <option value="expiring_30">Expiring in 30 Days</option>
              <option value="expiring_90">Expiring in 90 Days</option>
              <option value="expired">Expired</option>
              <option value="active">Active (90+ days)</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Assets Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No warranty alerts</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Asset</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-left p-4 font-medium">Vendor</th>
                    <th className="text-left p-4 font-medium">Warranty Expires</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {assets.map((asset) => (
                    <tr 
                      key={asset.id} 
                      className={cn(
                        "hover:bg-muted/50",
                        asset.daysUntilExpiry <= 30 && "bg-red-50/50 dark:bg-red-950/20"
                      )}
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{asset.name}</p>
                          <p className="text-sm text-muted-foreground font-mono">{asset.assetTag}</p>
                          {asset.serialNumber && (
                            <p className="text-xs text-muted-foreground">S/N: {asset.serialNumber}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span>{asset.category?.name || '-'}</span>
                        {asset.brand && (
                          <p className="text-sm text-muted-foreground">{asset.brand.name}</p>
                        )}
                      </td>
                      <td className="p-4">{asset.vendor?.name || '-'}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{formatDate(asset.warrantyExpiration)}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {getWarrantyBadge(asset)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/assets/${asset.id}`}>View Asset</Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">Page {page} of {pagination.totalPages}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
